// Business tenants + memberships for Greenback Business
// (/api/v1/business/businesses, /memberships, /invitations).
//
// listBusinesses/createBusiness/invitations operate ABOVE the business level
// (plain apiGet/apiPost — no X-Business-Id); membership listing is scoped to
// the active business (businessApiGet injects the header).

import {
  apiGet,
  apiPost,
  businessApiGet,
  businessApiPost,
  businessApiPatch,
  businessApiPut,
  businessApiDelete,
} from '../utils/apiUtils';
import { parseData, parseEnvelope } from './businessApiCommon';
import { featureFlag } from './featureFlagClient';
import { BUSINESS_ACCOUNTING_FLAG } from '../hooks/useBusinessDataMode';
import {
  BOOKS_ROLES,
  READ_RESTRICTED_ROLES,
} from '../components/business/settings/roleCopy';
import {
  Business,
  BusinessInvitation,
  BusinessInvitationAcceptance,
  BusinessInvitationClaim,
  BusinessInvitationLookup,
  BusinessGroupAddition,
  BusinessList,
  BusinessMemberInvitation,
  BusinessMembership,
  BusinessMembershipList,
  BusinessRole,
  ClosePasswordParams,
  CreateBusinessParams,
  UpdateBusinessParams,
  UUID,
} from '../types/businessApi';
import { withFieldSnapshot } from '../components/fieldService/offline/fieldSnapshots';

const BASE = '/api/v1/business';
const JSON_HEADERS = { 'Content-Type': 'application/json' };

class BusinessService {
  /**
   * GET /businesses — businesses I belong to, with my role and access_mode on
   * each, plus meta.can_create_business (the caller's own SKU entitlement),
   * meta.group_additions (groups they may add a business to, priced) and
   * meta.paid_empty_group_id (a group they paid for and have not named a
   * business in yet, PRD 86 §3).
   *
   * With no signal, a field service member's last list comes back from their
   * phone's copy (PRD 95 §4.13), so the technician page opens in a basement.
   * Kept only for someone in a business that holds field service.
   */
  async listBusinesses(): Promise<BusinessList> {
    return withFieldSnapshot('businesses', 'list', () => this.fetchBusinesses(),
      list => (list.businesses.some(business => business.modules?.field_service === true) ? 'keep' : 'skip'));
  }

  async fetchBusinesses(): Promise<BusinessList> {
    const response = await apiGet(`${BASE}/businesses`, { headers: JSON_HEADERS });
    const { data, meta } = await parseEnvelope<Business[]>(response);
    return {
      businesses: data ?? [],
      canCreateBusiness: meta.can_create_business === true,
      // Absent from an api that predates it — read as "nothing to offer".
      groupAdditions: Array.isArray(meta.group_additions)
        ? (meta.group_additions as BusinessGroupAddition[])
        : [],
      // Absent from an api before api#853 — read as "no paid group to name".
      paidEmptyGroupId:
        typeof meta.paid_empty_group_id === 'string' ? meta.paid_empty_group_id : null,
    };
  }

  /**
   * POST /businesses — creates the business, makes the caller its owner and
   * seeds the Chart of Accounts from `coa_template` (default general_business).
   */
  async createBusiness(params: CreateBusinessParams): Promise<Business> {
    const response = await apiPost(`${BASE}/businesses`, params, { headers: JSON_HEADERS });
    return parseData<Business>(response);
  }

  /** PATCH /businesses/:id — owner-only profile edit (X-Business-Id scoped). */
  async updateBusiness(businessId: UUID, params: UpdateBusinessParams): Promise<Business> {
    const response = await businessApiPatch(`${BASE}/businesses/${businessId}`, params);
    return parseData<Business>(response);
  }

  /**
   * PUT /businesses/:id/close-password — set, change or clear the optional
   * reopen password (PRD 08 §7.1). Changing or clearing an existing one
   * requires current_close_password; a blank close_password clears it.
   */
  async setClosePassword(
    businessId: UUID,
    params: ClosePasswordParams
  ): Promise<{ close_password_set: boolean }> {
    const response = await businessApiPut(`${BASE}/businesses/${businessId}/close-password`, params);
    return parseData<{ close_password_set: boolean }>(response);
  }

  /** GET /memberships — members of the ACTIVE business (X-Business-Id). */
  async listMemberships(): Promise<BusinessMembership[]> {
    const response = await businessApiGet(`${BASE}/memberships`);
    return parseData<BusinessMembership[]>(response);
  }

  /**
   * GET /memberships with meta.grantable_roles (PRD 89 §4.13): the members,
   * and the roles an owner may give in this business today. The api decides
   * the second (BusinessMembership::ROLE_PREREQUISITES — the field roles only
   * while field service is set up and entitled), so the Members tab offers
   * exactly what the api will accept.
   *
   * An api that predates the meta sends none, which reads as the six books
   * roles — the ones it knows. A role this client does not know is dropped:
   * it could not be labelled, let alone explained.
   */
  async listMembershipsWithGrants(): Promise<BusinessMembershipList> {
    const response = await businessApiGet(`${BASE}/memberships`);
    const { data, meta } = await parseEnvelope<BusinessMembership[]>(response);
    const known: readonly string[] = [...BOOKS_ROLES, ...READ_RESTRICTED_ROLES];
    const grantableRoles = Array.isArray(meta.grantable_roles)
      ? (meta.grantable_roles as unknown[]).filter(
          (role): role is BusinessRole => typeof role === 'string' && known.includes(role)
        )
      : [...BOOKS_ROLES];
    return { memberships: data ?? [], grantableRoles };
  }

  /** PATCH /memberships/:id — owner-only role change. */
  /**
   * PUT /businesses/:id/auto-categorization — owner or accountant.
   *
   * The only gate the auto-categorization module has: off here means no rules
   * fire, no history suggestions, and no learned proposals for this business.
   * Rules already written are left alone, so switching back on resumes them.
   */
  async setAutoCategorization(businessId: UUID, enabled: boolean): Promise<Business> {
    const response = await businessApiPut(
      `${BASE}/businesses/${businessId}/auto-categorization`,
      { enabled }
    );
    return parseData<Business>(response);
  }

  async updateMembershipRole(membershipId: UUID, role: BusinessRole): Promise<BusinessMembership> {
    const response = await businessApiPatch(`${BASE}/memberships/${membershipId}`, { role });
    return parseData<BusinessMembership>(response);
  }

  /** DELETE /memberships/:id — owner revokes a member, or a member leaves. */
  async removeMembership(membershipId: UUID): Promise<void> {
    const response = await businessApiDelete(`${BASE}/memberships/${membershipId}`);
    if (!response.ok) await parseData(response); // throws BusinessApiError
  }

  // ---- Invitations the ACTIVE business has sent (owner view, scoped) ----

  /** GET /member-invitations */
  async listMemberInvitations(): Promise<BusinessMemberInvitation[]> {
    const response = await businessApiGet(`${BASE}/member-invitations`);
    return parseData<BusinessMemberInvitation[]>(response);
  }

  /**
   * POST /member-invitations — invite by email, whether or not that address
   * has a Greenback account yet (PRD 28).
   */
  async inviteMember(email: string, role: BusinessRole): Promise<BusinessMemberInvitation> {
    const response = await businessApiPost(`${BASE}/member-invitations`, { email, role });
    return parseData<BusinessMemberInvitation>(response);
  }

  /** POST /member-invitations/:id/resend — rotates the token, resets expiry. */
  async resendMemberInvitation(invitationId: UUID): Promise<BusinessMemberInvitation> {
    const response = await businessApiPost(
      `${BASE}/member-invitations/${invitationId}/resend`,
      undefined
    );
    return parseData<BusinessMemberInvitation>(response);
  }

  /** DELETE /member-invitations/:id — revoke; the token dies immediately. */
  async revokeMemberInvitation(invitationId: UUID): Promise<void> {
    const response = await businessApiDelete(`${BASE}/member-invitations/${invitationId}`);
    if (!response.ok) await parseData(response); // throws BusinessApiError
  }

  // ---- Invitations addressed to ME (not business-scoped) ----

  /** GET /invitations */
  async listInvitations(): Promise<BusinessInvitation[]> {
    const response = await apiGet(`${BASE}/invitations`, { headers: JSON_HEADERS });
    return parseData<BusinessInvitation[]>(response);
  }

  /** POST /invitations/:id/accept */
  async acceptInvitation(invitationId: UUID): Promise<BusinessInvitationAcceptance> {
    const response = await apiPost(`${BASE}/invitations/${invitationId}/accept`, undefined, {
      headers: JSON_HEADERS,
    });
    const accepted = await parseData<BusinessInvitationAcceptance>(response);
    forgetBusinessMode();
    return accepted;
  }

  /** POST /invitations/:id/decline */
  async declineInvitation(invitationId: UUID): Promise<void> {
    const response = await apiPost(`${BASE}/invitations/${invitationId}/decline`, undefined, {
      headers: JSON_HEADERS,
    });
    await parseData(response);
  }

  // ---- The emailed link (public; no auth, no business scope) ----

  /**
   * GET /invitations/lookup — what the landing page needs to decide which of
   * its states to render. Never carries business data beyond the name.
   */
  async lookupInvitation(token: string): Promise<BusinessInvitationLookup> {
    const response = await apiGet(
      `${BASE}/invitations/lookup?token=${encodeURIComponent(token)}`,
      { headers: JSON_HEADERS, skipAuth: true }
    );
    return parseData<BusinessInvitationLookup>(response);
  }

  /** POST /invitations/accept-by-token — signed in as the invited address. */
  async acceptInvitationByToken(token: string): Promise<BusinessInvitationAcceptance> {
    const response = await apiPost(
      `${BASE}/invitations/accept-by-token`,
      { token },
      { headers: JSON_HEADERS }
    );
    const accepted = await parseData<BusinessInvitationAcceptance>(response);
    forgetBusinessMode();
    return accepted;
  }

  /** POST /invitations/claim — no account yet: create one and join. */
  async claimInvitation(
    token: string,
    firstName: string,
    lastName: string
  ): Promise<BusinessInvitationClaim> {
    const response = await apiPost(
      `${BASE}/invitations/claim`,
      { token, first_name: firstName, last_name: lastName },
      { headers: JSON_HEADERS, skipAuth: true }
    );
    const claimed = await parseData<BusinessInvitationClaim>(response);
    forgetBusinessMode();
    return claimed;
  }
}

/**
 * Accepting an invitation makes this person a member of real books, and the
 * api answers `business_accounting` true for every member (api#766) — but the
 * flag client may still hold the preview answer it fetched before, for up to
 * its TTL. Forgetting it means the next /business mount asks again and opens
 * the books rather than a sample-data preview over them.
 */
function forgetBusinessMode(): void {
  featureFlag.invalidate([BUSINESS_ACCOUNTING_FLAG]);
}

export const businessService = new BusinessService();
